"use client";

import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/ui/dialog";
import { RootState } from "@store/store";
import { 
  acceptCookies, 
  rejectCookies 
} from "@store/features/cookieConsentSlice";
import { useStyleSchema } from "@components/styleSchema/StyleSchemaHandler";

export function CookieConsentModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentSchema } = useStyleSchema();
  
  const { isConsentModalOpen } = useSelector((state: RootState) => state.cookieConsent);
  
  const buttonColor = currentSchema.colors?.primary?.hex;
  const privacyLink = currentSchema.terms?.privacyLink || "https://www.klier.de/datenschutz/";

  const handleAccept = () => {
    dispatch(acceptCookies());
  };

  const handleReject = () => {
    dispatch(rejectCookies());
  };

  return (
    <Dialog 
      modal={true} 
      open={isConsentModalOpen} 
      onOpenChange={() => {}} // Prevent closing without decision
    >
      <DialogContent 
        className="bg-white md:max-w-2xl max-h-[90vh] overflow-y-auto mx-auto w-[90%] rounded-lg p-6 [&>button]:!hidden"
        onPointerDownOutside={(e) => {
          e.preventDefault(); // Prevent closing by clicking outside
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault(); // Prevent closing with escape key
        }}
      >
        <DialogHeader className="mb-4">
          <DialogTitle className="text-xl font-semibold">
            {t("cookies.title")}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-sm text-gray-700">
            <p>{t("cookies.description")}</p>
          </div>
          
          <div className="text-sm text-gray-600">
            <p>{t("cookies.technical-note")}</p>
          </div>
          
          <div className="text-sm">
            <p>
              {t("cookies.privacy-link-text")}{" "}
              <a 
                href={privacyLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:underline"
                style={{ color: buttonColor }}
              >
                {t("cookies.privacy-link-label")}
              </a>.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              onClick={handleReject}
              className="flex-1 px-4 py-2 border rounded-md text-center font-medium hover:bg-gray-50 transition-colors"
              style={{
                borderColor: buttonColor,
                color: buttonColor,
              }}
            >
              {t("cookies.reject")}
            </button>
            
            <button
              onClick={handleAccept}
              className="flex-1 px-4 py-2 rounded-md text-center font-medium text-white hover:opacity-90 transition-opacity"
              style={{
                backgroundColor: buttonColor,
              }}
            >
              {t("cookies.accept")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 